import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import FrameList from './FrameList for PrintandFramewithMatboardandGlass';
import FrameStickSpecPanel from './FrameStickSpecPanel';
import MatList from './MatList';
import { totalPrice as calculatePrice } from './PricingEngine';
import {
  CUSTOM_SIZE_OPTION,
  customSizeOptions,
  parseCustomDims,
  getImageSizeInches,
  getPrintAreaInches,
  getEffectiveOpeningCm,
  nearestTierForPricing as computeNearestPrintTier,
  computePreviewCanvasSize,
  getCustomSizeMetaString
} from './customPrintSize';
import Swal from 'sweetalert2';

function PrintFrameMatboard({ image, originalImage, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const uploadedUserImage = originalImage || userImage;
  const canvasRef = useRef(null);
  /* ================= STATES ================= */
  const [printType, setPrintType] = useState('canvas');
  const [glassType, setGlassType] = useState('');
  const [frameOpen, setFrameOpen] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, setProgress] = useState(0);
  const [backingType, setBackingType] = useState('');
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);
  const [customSize, setCustomSize] = useState(CUSTOM_SIZE_OPTION);
  const [customWidthCm, setCustomWidthCm] = useState('');
  const [customHeightCm, setCustomHeightCm] = useState('');
  const [appliedCustomCropKey, setAppliedCustomCropKey] = useState('');
  const { widthIn, heightIn, normalizedSize } =
    getImageSizeInches(imageSizeCm);
  const MM_PER_INCH = 25.4;
  const mmToPx = (mm, dpi = DPI) => {
    return Math.round((mm / MM_PER_INCH) * dpi);
  };
  /* ================= SPACER ================= */
  const spacerOptions = [
    {
      id: 'no_spacer',
      label: 'No Spacer',
      Width_mm: 0,
      price: 0
    },
    {
      id: 'spacer_matboard',
      label: 'Spacer Matboard',
      Width_mm: 34,
      price: 20
    },
    {
      id: 'clear_plastic_white',
      label: 'Clear Plastic white',
      Width_mm: 4,
      price: 5
    },
    {
      id: 'clear_plastic_black',
      label: 'Clear Plastic Black',
      Width_mm: 4,
      price: 5
    },
    {
      id: 'clear_plastic_transparent',
      label: 'Clear Plastic Transparent',
      Width_mm: 4,
      price: 5
    }
  ];
  // State to track selected spacer
  const [spacerType, setSpacerType] = useState('no');

  /* ================= V-GROOVE (MAT 1 ONLY) ================= */
  const vGrooveOptions = [
    { id: 'no', label: 'No V-Groove', price: 0 },
    { id: 'yes', label: 'Yes V-Groove', price: 15 }
  ];
  const [vGrooveType, setVGrooveType] = useState('no');

  const makeGlassVariant = ({
    code,
    description,
    width,
    height,
    boxQty = null,
    boxPrice = null,
    listPrice,
    componentType = 'GlassSheet'
  }) => ({
    Code: code,
    Description: description,
    Width_mm: width,
    Height_mm: height,
    Box_Qty: boxQty,
    Box_Price: boxPrice,
    ListPrice_per_sheet: listPrice,
    code,
    description,
    width,
    height,
    box_qty: boxQty,
    box_price: boxPrice,
    price: listPrice,
    sheet_price: listPrice,
    component_type: componentType,
    purchase_unit: 'Sheet',
    wastage: 30
  });

  const glassOptions = [
    { id: 'no_glass', label: 'No Glass', variants: [] },
    {
      id: 'standard_clear_glass',
      label: 'Standard Clear Glass',
      variants: [
        makeGlassVariant({
          code: 'GL36X48BOX',
          description: 'EZIPAK Glass 2mm 36" x 48" (914mm x 1220mm)',
          width: 914,
          height: 1219,
          boxQty: 4,
          boxPrice: 109,
          listPrice: 27.25
        })
      ]
    },
    {
      id: 'non_reflective',
      label: 'Non Reflective',
      variants: [
        makeGlassVariant({
          code: 'GLNR36X48BOX',
          description: 'EZIPAK Non-Reflective 2mm Glass 36" x 48" (914mm x 1220mm)',
          width: 914,
          height: 1219,
          boxQty: 4,
          boxPrice: 182,
          listPrice: 45.5
        })
      ]
    },
    {
      id: 'standard_conservation_tru_vue',
      label: 'Standard Conservation Tru Vue',
      variants: [
        makeGlassVariant({
          code: 'TVCC32X40',
          description: 'Tru Vue Conservation Clear 32x40 (6 sheets)',
          width: 813,
          height: 1016,
          boxQty: 6,
          boxPrice: 365,
          listPrice: 60.83333333
        }),
        makeGlassVariant({
          code: 'TVCC36X48',
          description: 'TRU VUE CONSERVATION CLEAR 36X48 (4 SHTS)',
          width: 914,
          height: 1219,
          boxQty: 4,
          boxPrice: 355,
          listPrice: 88.75
        })
      ]
    },
    {
      id: 'tru_vue_reflection_control',
      label: 'Tru Vue Reflection Control',
      variants: [
        makeGlassVariant({
          code: 'TVRC36X48',
          description: 'TRU VUE REFLECTION CONTROL 36X48 (4 SHTS)',
          width: 914,
          height: 1219,
          boxQty: 4,
          boxPrice: 370,
          listPrice: 92.5
        }),
        makeGlassVariant({
          code: 'TVRC40X60',
          description: 'TRU VUE REFLECTION CONTROL 40X60 (2 SHTS)',
          width: 1016,
          height: 1524,
          boxQty: 2,
          boxPrice: 355,
          listPrice: 177.5
        })
      ]
    },
    {
      id: 'acrylic_clear',
      label: 'Acrylic',
      variants: [
        makeGlassVariant({
          code: 'Hui Hong PSPX2.40X60',
          description: 'Antons Clear Acrylic - 2mm',
          width: 1016,
          height: 1524,
          listPrice: 60,
          componentType: 'AcrylicSheet'
        }),
        makeGlassVariant({
          code: 'ACR3.48X96',
          description: 'Antons Clear Acrylic - 3mm',
          width: 1219,
          height: 2438,
          listPrice: 174,
          componentType: 'AcrylicSheet'
        })
      ]
    },
    {
      id: 'acrylic_conservation',
      label: 'Acrylic Conservation',
      variants: [
        makeGlassVariant({
          code: 'TVACC48X96',
          description: 'TRU VUE CONSERVATION CLEAR ACRYLITE 3MM 48X96 (1 SHEET)',
          width: 1219,
          height: 2438,
          boxQty: 1,
          boxPrice: 510,
          listPrice: 510,
          componentType: 'AcrylicSheet'
        })
      ]
    },
    {
      id: 'acrylic_reflection_control',
      label: 'Acrylic Reflection Control',
      variants: [
        makeGlassVariant({
          code: 'TVACRC48X96',
          description: 'TRU VUE CONSERVATION REFLECTION CONTROL 3MM 48X96 (1 SHEET)',
          width: 1219,
          height: 2438,
          boxQty: 1,
          boxPrice: 580,
          listPrice: 580,
          componentType: 'AcrylicSheet'
        })
      ]
    },
    {
      id: 'acrylic_museum',
      label: 'Acrylic Museum',
      variants: [
        makeGlassVariant({
          code: 'TVOMA32X40',
          description: 'Optimum Museum Acrylic 32x40 3mm Conversation Grade(3perbox)',
          width: 813,
          height: 1016,
          boxQty: 3,
          boxPrice: 2195,
          listPrice: 731.6666667,
          componentType: 'AcrylicSheet'
        })
      ]
    },
    {
      id: 'museum',
      label: 'Museum',
      variants: [
        makeGlassVariant({
          code: 'TVMG32X40',
          description: 'TRU VUE MUSEUM GLASS 32 X 40 (1 SHT)',
          width: 813,
          height: 1016,
          boxQty: 1,
          boxPrice: 259,
          listPrice: 259
        }),
        makeGlassVariant({
          code: 'TVMG48X68',
          description: 'TRU VUE MUSEUM GLASS 48 X 68 (2 SHEETS PER BOX)',
          width: 1219,
          height: 1727,
          boxQty: 2,
          boxPrice: 1150,
          listPrice: 575
        })
      ]
    },
    {
      id: 'art_glass',
      label: 'Art Glass',
      variants: [
        makeGlassVariant({
          code: 'AG70AR32X40',
          description: 'Artglass AR70 Glass 2mm 32x40 (4 sheets/pack)',
          width: 813,
          height: 1016,
          boxQty: 4,
          boxPrice: 414,
          listPrice: 103.5
        }),
        makeGlassVariant({
          code: 'AG70AR40X60',
          description: 'Artglass AR70 Glass 2mm 40x60 (2 sheets/pack)',
          width: 1016,
          height: 1524,
          boxQty: 2,
          boxPrice: 414,
          listPrice: 207
        })
      ]
    }
  ];

  /* ================= FRAME OPTIONS ================= */
  const frameOptions = FrameList;

  /* ================= MAT OPTIONS ================= */
  const matOptions = MatList;

  const backingOptions = [
    { id: 'none', label: 'No Backing' },
    { id: 'adhesive_foamcore', label: 'Adhesive Foamcore' },
    { id: 'non_adhesive_foamcore', label: 'Non-Adhesive Foamcore' }
  ];

  const [activeMatIndex, setActiveMatIndex] = useState(0);
  const [mats, setMats] = useState([
    {
      mat: null,
      mode: 'equal', // 'equal' | 'custom'
      thickness: 0,  // used for equal
      custom: { top: 3, bottom: 3, left: 3, right: 3 } // used for custom
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 1, bottom: 1, left: 1, right: 1 }
    },
    {
      mat: null,
      mode: 'equal',
      thickness: 0,
      custom: { top: 1, bottom: 1, left: 1, right: 1 }
    }
  ]);
  const MAT_MIN_CM = 3;
  const MAT_ADDITIONAL_MIN_CM = 1;
  const MAT_MAX_CM = 15;
  const MAT_STEP_CM = 1;
  const getMatMinCm = index => (
    index === 0 ? MAT_MIN_CM : MAT_ADDITIONAL_MIN_CM
  );

  const updateMat = (index, data) => {
    setMats(prev => {
      const copy = [...prev];

      copy[index] = {
        mat: copy[index]?.mat || null,
        mode: copy[index]?.mode || 'equal',
        thickness: copy[index]?.thickness || 0,
        custom: copy[index]?.custom || { top: 0, bottom: 0, left: 0, right: 0 },
        ...data
      };

      return copy;
    });
  };

  const hasMat = index => mats[index]?.mat !== null;

  useEffect(() => {
    renderCanvas();
  }, [printType, selectedFrame, mats, glassType, userImage, customSize, customWidthCm, customHeightCm, imageSizeCm]);

  /* ================= PRICES ================= */
  const CM_TO_MM = 10;
  const DEFAULT_SHEET_WIDTH_MM = 1000;
  const DEFAULT_SHEET_HEIGHT_MM = 1500;

  const selectedGlass = glassOptions.find(g => g.id === glassType);
  const selectedBacking = backingOptions.find(b => b.id === backingType);

  const selectedMatLayer = mats.find(m => m?.mat);
  const selectedMat = selectedMatLayer?.mat || null;

  const cmToInches = (cm) => {
    const parsed = parseFloat(cm);
    if (!parsed || parsed <= 0) return 0;

    return parsed / 2.54;
  };
  const customDimsParsed = () => {
    const widthInches = cmToInches(customWidthCm);
    const heightInches = cmToInches(customHeightCm);

    return parseCustomDims(widthInches, heightInches);
  };
  const isCustomWhMode = customSize === CUSTOM_SIZE_OPTION;
  const customDims = isCustomWhMode ? customDimsParsed() : null;
  const customWidthValue = Number(customWidthCm);
  const customHeightValue = Number(customHeightCm);
  const hasValidCustomSize =
    Number.isFinite(customWidthValue) &&
    Number.isFinite(customHeightValue) &&
    customWidthValue > 0 &&
    customHeightValue > 0;
  const customCropKey = hasValidCustomSize
    ? `${customWidthValue}x${customHeightValue}`
    : '';
  const showApplyCustomCrop =
    isCustomWhMode && hasValidCustomSize && customCropKey !== appliedCustomCropKey;

  const handleApplyCustomCrop = () => {
    if (!showApplyCustomCrop || !onEditImage) return;

    setAppliedCustomCropKey(customCropKey);
    onEditImage({
      aspect: customWidthValue / customHeightValue,
      customWidthCm: customWidthValue,
      customHeightCm: customHeightValue
    });
  };
  const effOpeningCm = getEffectiveOpeningCm(customSize, customDims, imageSizeCm);

  const openingWidthCm =
    Number(effOpeningCm.widthCm) > 0
      ? effOpeningCm.widthCm
      : Number(imageSizeCm?.widthCm) || 30;
  const openingHeightCm =
    Number(effOpeningCm.heightCm) > 0
      ? effOpeningCm.heightCm
      : Number(imageSizeCm?.heightCm) || 30;
  const openingWidthMm = openingWidthCm * CM_TO_MM;
  const openingHeightMm = openingHeightCm * CM_TO_MM;

  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);
  const { previewCanvasW, previewCanvasH } = computePreviewCanvasSize(area);

  const nearestTier = computeNearestPrintTier({
    printType,
    customSize,
    customDims,
    widthIn,
    heightIn
  });

  const getArtworkSizeMm = () => ({
    widthMm: openingWidthMm,
    heightMm: openingHeightMm
  });

  const selectSheetVariant = (option, requiredWidthMm, requiredHeightMm) => {
    if (!option?.variants?.length) return null;

    const shortSide = Math.min(requiredWidthMm, requiredHeightMm);
    const longSide = Math.max(requiredWidthMm, requiredHeightMm);
    const sortedVariants = [...option.variants].sort((a, b) => {
      const areaA = Number(a.width ?? a.Width_mm) * Number(a.height ?? a.Height_mm);
      const areaB = Number(b.width ?? b.Width_mm) * Number(b.height ?? b.Height_mm);
      return areaA - areaB;
    });

    return sortedVariants.find((variant) => {
      const variantWidth = Number(variant.width ?? variant.Width_mm);
      const variantHeight = Number(variant.height ?? variant.Height_mm);
      const variantShortSide = Math.min(variantWidth, variantHeight);
      const variantLongSide = Math.max(variantWidth, variantHeight);

      return shortSide <= variantShortSide && longSide <= variantLongSide;
    }) || sortedVariants[sortedVariants.length - 1];
  };

  const artworkSize = getArtworkSizeMm();
  const selectedGlassVariant = selectSheetVariant(
    selectedGlass,
    artworkSize.widthMm,
    artworkSize.heightMm
  );
  const selectedGlassPrice = Number(
    selectedGlassVariant?.ListPrice_per_sheet ?? selectedGlassVariant?.price ?? 0
  );

  const buildEngineMat = () => {
    if (!selectedMatLayer || !selectedMat) return {};

    if (selectedMatLayer.mode === 'custom') {
      const top = Number(selectedMatLayer.custom?.top || 0);
      const bottom = Number(selectedMatLayer.custom?.bottom || 0);
      const left = Number(selectedMatLayer.custom?.left || 0);
      const right = Number(selectedMatLayer.custom?.right || 0);
      const side = (left + right) / 2;

      return {
        ...selectedMat,
        top: top * CM_TO_MM,
        bottom: bottom * CM_TO_MM,
        side: side * CM_TO_MM,
        width: selectedMat.width || DEFAULT_SHEET_WIDTH_MM,
        height: selectedMat.height || DEFAULT_SHEET_HEIGHT_MM,
        sheet_price: selectedMat.sheet_price || selectedMat.price
      };
    }

    const thicknessCm = Number(selectedMatLayer.thickness || 0);
    const sideMm = thicknessCm * CM_TO_MM;

    return {
      ...selectedMat,
      top: sideMm,
      bottom: sideMm,
      side: sideMm,
      width: selectedMat.width || DEFAULT_SHEET_WIDTH_MM,
      height: selectedMat.height || DEFAULT_SHEET_HEIGHT_MM,
      sheet_price: selectedMat.sheet_price || selectedMat.price
    };
  };

  const buildSheetMaterial = item => {
    if (!item || item.id === 'none' || item.id === 'no_glass') return {};
    return {
      ...item,
      width: item.width ?? item.Width_mm ?? DEFAULT_SHEET_WIDTH_MM,
      height: item.height ?? item.Height_mm ?? DEFAULT_SHEET_HEIGHT_MM,
      sheet_price: item.ListPrice_per_sheet ?? item.sheet_price ?? item.price
    };
  };

  const spacerPrice = spacerOptions.find(s => s.id === spacerType)?.price || 0;
  // Add spacer and v-groove charges manually
  const vGroovePrice = vGrooveType === 'yes' ? 15 : 0;


  const { finalPrice: calculatedFinalPrice, basePrice } = calculatePrice({
    qty: 1,
    opening_width: openingWidthMm,
    opening_height: openingHeightMm,
    print_type: printType,
    frame: selectedFrame || {},
    mat: buildEngineMat(),
    glass: {},
    backing: buildSheetMaterial(selectedBacking),
    extras: [
      { type: 'v_groove', option: vGrooveType }
    ],
    openings: 1,
    discount: 0,
  });

  /* Add glass, spacer, and v-groove manually */
  const finalPrice = calculatedFinalPrice + selectedGlassPrice + spacerPrice + vGroovePrice;
  /* ================= GST ================= */
  const GST_RATE = 0.1;

  const gstAmount = finalPrice * GST_RATE;
  const totalWithGST = finalPrice + gstAmount;

  /* ================= FILTER ================= */
  const getPrintFilter = () => {
    switch (printType) {
      case 'canvas': return 'contrast(1.05) saturate(1.1)';
      case 'archival': return 'contrast(1.05) saturate(0.95)';
      case 'art-decor': return 'contrast(0.95) saturate(0.9) brightness(0.98)';
      default: return 'none';
    }
  };

  const renderGlassEffect = (ctx, W, H, frameT) => {
    if (glassType === 'no_glass') return;

    const x = frameT;
    const y = frameT;
    const w = W - frameT * 2;
    const h = H - frameT * 2;

    ctx.save();

    /* ================= NORMAL GLASS ================= */
    if (glassType === 'standard_clear_glass') {
      // subtle reflection + glare
      const grad = ctx.createLinearGradient(x, y, x + w, y + h);
      grad.addColorStop(0, 'rgba(255,255,255,0.08)');
      grad.addColorStop(0.3, 'rgba(255,255,255,0.02)');
      grad.addColorStop(0.6, 'rgba(255,255,255,0.06)');
      grad.addColorStop(1, 'rgba(255,255,255,0.12)');

      ctx.fillStyle = grad;
      ctx.fillRect(x, y, w, h);
    }

    /* ================= NON-REFLECTIVE ================= */
    if (glassType === 'non_reflective') {
      // clarity + light diffusion
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, w, h);

      // micro blur simulation
      ctx.globalAlpha = 0.04;
      ctx.fillStyle = '#dcdcdc';
      ctx.fillRect(x, y, w, h);
    }

    /* ================= MUSEUM UV ================= */
    if (
      glassType === 'standard_conservation_tru_vue' ||
      glassType === 'tru_vue_reflection_control' ||
      glassType === 'acrylic_conservation' ||
      glassType === 'acrylic_reflection_control' ||
      glassType === 'acrylic_museum' ||
      glassType === 'museum' ||
      glassType === 'art_glass'
    ) {
      // UV tint + crystal clarity
      ctx.globalAlpha = 0.05;
      ctx.fillStyle = '#e8f3ff'; // cool UV tone
      ctx.fillRect(x, y, w, h);

      // premium reflection line
      ctx.globalAlpha = 0.08;
      const uvGrad = ctx.createLinearGradient(x, y, x + w, y);
      uvGrad.addColorStop(0, 'rgba(255,255,255,0)');
      uvGrad.addColorStop(0.5, 'rgba(255,255,255,0.12)');
      uvGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = uvGrad;
      ctx.fillRect(x, y, w, h);
    }

    /* ================= CLEAR PERSPEX ================= */
    if (glassType === 'acrylic_clear') {
      // plastic clarity + shine
      ctx.globalAlpha = 0.10;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, w, h);

      // highlight streak
      ctx.globalAlpha = 0.15;
      const pGrad = ctx.createLinearGradient(x, y, x + w, y + h);
      pGrad.addColorStop(0, 'rgba(255,255,255,0.25)');
      pGrad.addColorStop(0.2, 'rgba(255,255,255,0)');
      ctx.fillStyle = pGrad;
      ctx.fillRect(x, y, w, h);
    }

    ctx.restore();
  };

  /* ================= CANVAS ENGINE ================= */
  /* ================= CANVAS ENGINE ================= */
  const renderIdRef = useRef(0);

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        resolve(null);
        return;
      }

      const image = new Image();
      image.crossOrigin = 'anonymous';

      image.onload = () => resolve(image);
      image.onerror = reject;

      image.src = src;
    });
  };

  const renderCanvas = async () => {
    if (!userImage || !canvasRef.current) return;

    const currentRenderId = ++renderIdRef.current;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    try {
      /* ================= LOAD MAIN IMAGE ================= */
      const img = await loadImage(userImage);

      // stop outdated render
      if (currentRenderId !== renderIdRef.current) return;

      /* ================= FRAME ================= */
      const framePx = Number(selectedFrame?.thickness || 0);

      const selectedSpacer = spacerOptions.find(
        s => s.id === spacerType
      );

      const spacerPx =
        selectedSpacer?.Width_mm > 0
          ? mmToPx(selectedSpacer.Width_mm)
          : 0;

      /* ================= BACKGROUND ================= */
      if (framePx > 0) {
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, W, H);
      }

      let insetPx = framePx + spacerPx;

      /* ================= SPACER ================= */
      if (spacerPx > 0) {
        ctx.save();

        ctx.fillStyle = 'rgba(0,0,0,0.12)';

        ctx.fillRect(
          framePx,
          framePx,
          W - framePx * 2,
          H - framePx * 2
        );

        ctx.restore();
      }

      /* ================= MATS ================= */
      for (const m of mats) {
        if (!m.mat) continue;

        let topPx = 0;
        let bottomPx = 0;
        let leftPx = 0;
        let rightPx = 0;

        const PREVIEW_PX_PER_CM = 10;

        if (m.mode === 'equal') {
          const px = m.thickness * PREVIEW_PX_PER_CM;

          topPx = px;
          bottomPx = px;
          leftPx = px;
          rightPx = px;
        } else {
          topPx = m.custom.top * PREVIEW_PX_PER_CM;
          bottomPx = m.custom.bottom * PREVIEW_PX_PER_CM;
          leftPx = m.custom.left * PREVIEW_PX_PER_CM;
          rightPx = m.custom.right * PREVIEW_PX_PER_CM;
        }

        const outerX = insetPx;
        const outerY = insetPx;
        const outerW = W - insetPx * 2;
        const outerH = H - insetPx * 2;

        if (outerW <= 0 || outerH <= 0) break;

        /* ===== LOAD MAT TEXTURE ===== */
        if (m.mat.img) {
          const matImg = await loadImage(m.mat.img);

          if (currentRenderId !== renderIdRef.current) return;

          if (matImg) {
            const pattern = ctx.createPattern(matImg, 'repeat');

            ctx.fillStyle = pattern;
          } else {
            ctx.fillStyle = '#f4f4f4';
          }
        } else {
          ctx.fillStyle = '#f4f4f4';
        }

        /* ===== DRAW MAT ===== */
        ctx.fillRect(
          outerX,
          outerY,
          outerW,
          outerH
        );

        /* ===== CUT OPENING ===== */
        const innerX = insetPx + leftPx;
        const innerY = insetPx + topPx;

        const innerW =
          outerW - leftPx - rightPx;

        const innerH =
          outerH - topPx - bottomPx;

        if (innerW > 0 && innerH > 0) {
          ctx.clearRect(
            innerX,
            innerY,
            innerW,
            innerH
          );
        }

        insetPx += Math.max(
          topPx,
          bottomPx,
          leftPx,
          rightPx
        );
      }

      /* ================= IMAGE ================= */
      const imageW = W - insetPx * 2;
      const imageH = H - insetPx * 2;

      if (imageW > 0 && imageH > 0) {
        ctx.save();

        ctx.filter = getPrintFilter();

        ctx.drawImage(
          img,
          insetPx,
          insetPx,
          imageW,
          imageH
        );

        ctx.restore();
      }

      /* ================= GLASS ================= */
      renderGlassEffect(ctx, W, H, framePx);

      /* ================= FRAME TEXTURE ================= */
      if (selectedFrame?.img && framePx > 0) {
        const frameImg = await loadImage(selectedFrame.img);

        if (currentRenderId !== renderIdRef.current) return;

        if (frameImg) {
          const miter = framePx;

          /* ========================================================
             TOP FRAME
             ======================================================== */

          ctx.save();

          ctx.beginPath();

          // Outer top-left
          ctx.moveTo(0, 0);

          // Outer top-right
          ctx.lineTo(W, 0);

          // Inner top-right - 45° miter
          ctx.lineTo(W - miter, framePx);

          // Inner top-left - 45° miter
          ctx.lineTo(miter, framePx);

          ctx.closePath();

          ctx.clip();

          ctx.drawImage(
            frameImg,
            0,
            0,
            W,
            framePx
          );

          ctx.restore();


          /* ========================================================
             BOTTOM FRAME
             180° OPPOSITE OF TOP FRAME
             The moulding/profile is flipped toward the image
          ======================================================== */

          ctx.save();

          ctx.beginPath();

          // Inner bottom-left
          ctx.moveTo(miter, H - framePx);

          // Inner bottom-right
          ctx.lineTo(W - miter, H - framePx);

          // Outer bottom-right
          ctx.lineTo(W, H);

          // Outer bottom-left
          ctx.lineTo(0, H);

          ctx.closePath();

          ctx.clip();

          /*
            Draw the SAME texture upside down.
      
            We translate to the bottom-right corner,
            rotate 180 degrees, and draw the texture
            from the opposite direction.
          */
          ctx.translate(W, H);
          ctx.rotate(Math.PI);

          ctx.drawImage(
            frameImg,
            0,
            0,
            W,
            framePx
          );

          ctx.restore();


          /* ========================================================
             LEFT FRAME
             ======================================================== */

          ctx.save();

          ctx.beginPath();

          // Outer top-left
          ctx.moveTo(0, 0);

          // Inner top-left
          ctx.lineTo(framePx, miter);

          // Inner bottom-left
          ctx.lineTo(framePx, H - miter);

          // Outer bottom-left
          ctx.lineTo(0, H);

          ctx.closePath();

          ctx.clip();

          // Rotate texture 90° counter-clockwise
          ctx.translate(0, H);
          ctx.rotate(-Math.PI / 2);

          ctx.drawImage(
            frameImg,
            0,
            0,
            H,
            framePx
          );

          ctx.restore();


          /* ========================================================
             RIGHT FRAME
             ======================================================== */

          ctx.save();

          ctx.beginPath();

          // Inner top-right
          ctx.moveTo(W - framePx, miter);

          // Outer top-right
          ctx.lineTo(W, 0);

          // Outer bottom-right
          ctx.lineTo(W, H);

          // Inner bottom-right
          ctx.lineTo(W - framePx, H - miter);

          ctx.closePath();

          ctx.clip();

          // Rotate texture 90° clockwise
          ctx.translate(W, 0);
          ctx.rotate(Math.PI / 2);

          ctx.drawImage(
            frameImg,
            0,
            0,
            H,
            framePx
          );

          ctx.restore();
        }
      }
    } catch (err) {
      console.error('Canvas render failed:', err);
    }
  };


  const exportEditedImage = () => {
    return canvasRef.current.toDataURL('image/png');
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {

    if (isCustomWhMode && !hasValidCustomSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Print Size Required',
        text: 'Please enter the print size width and height in cm.'
      });
      return;
    }

    if (!customSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Custom Size Required',
        text: 'Please select custom size.'
      });
      return;
    }

    if (!glassType) {
      Swal.fire({
        icon: 'warning',
        title: 'Glass Required',
        text: 'Please select Glass'
      });
      return;
    }

    if (!selectedFrame) {
      Swal.fire({
        icon: 'warning',
        title: 'Frame Required',
        text: 'Please select Frame'
      });
      return;
    }

    if (!backingType) {
      Swal.fire({
        icon: 'warning',
        title: 'Backing Required',
        text: 'Please select Backing'
      });
      return;
    }

    if (!exportEditedImage) {
      alert("Image not ready.");
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      /* ================================
         CROPPED IMAGE (SINGLE SOURCE)
      ================================ */
      const croppedUserImage = userImage;
      let imageDataUrl = croppedUserImage;

      // If it's a URL, convert to base64 ONCE
      if (!imageDataUrl.startsWith('data:image')) {
        const res = await fetch(imageDataUrl);
        const blob = await res.blob();

        imageDataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }


      /* ================================
         PAYLOAD
      ================================ */
      const payload = {
        id: 1264,
        quantity: 1,
        meta_data: [
          /* ===== CORE ===== */
          { key: 'print_type', value: printType },
          {
            key: 'custom_size',
            value: getCustomSizeMetaString(
              normalizedSize,
              customSize,
              customDimsParsed()
            )
          },
          { key: 'print_nearest_tier', value: nearestTier.key },
          { key: 'glass_type', value: glassType },
          { key: 'glass_name', value: selectedGlass?.label || 'No Glass' },
          { key: 'glass_code', value: selectedGlassVariant?.code || '' },
          { key: 'glass_description', value: selectedGlassVariant?.description || '' },
          { key: 'glass_sheet_size_mm', value: selectedGlassVariant ? `${selectedGlassVariant.width}x${selectedGlassVariant.height}` : '' },
          { key: 'glass_sheet_price', value: selectedGlassPrice },
          { key: 'glass_price', value: selectedGlassPrice },
          { key: 'frame_name', value: selectedFrame?.name || 'No Frame' },

          /* ===== MATS ===== */
          ...mats.map((m, i) => {
            if (!m.mat) return [];

            const index = i + 1;

            if (m.mode === 'equal') {
              return [
                { key: `mat_${index}_name`, value: m.mat.name },
                { key: `mat_${index}_mode`, value: 'equal' },
                { key: `mat_${index}_thickness_cm`, value: m.thickness }
              ];
            } else {
              return [
                { key: `mat_${index}_name`, value: m.mat.name },
                { key: `mat_${index}_mode`, value: 'custom' },
                { key: `mat_${index}_top_cm`, value: m.custom.top },
                { key: `mat_${index}_bottom_cm`, value: m.custom.bottom },
                { key: `mat_${index}_left_cm`, value: m.custom.left },
                { key: `mat_${index}_right_cm`, value: m.custom.right }
              ];
            }
          }).flat(),


          /* ===== V-GROOVE ===== */
          { key: 'v_groove', value: vGrooveType === 'yes' ? 'Yes' : 'No' },

          /* ===== SPACER ===== */
          { key: 'spacer', value: spacerType === 'yes' ? 'Yes' : 'No' },

          /* ===== BACKING ===== */
          { key: 'backing_type', value: backingType },

          /* ===== PRICE ===== */
          { key: 'total_price', value: totalWithGST.toFixed(2) },
          { key: 'base_price', value: basePrice.toFixed(2) },

          /* ===== IMAGE (FINAL) ===== */
          { key: 'edited_image', value: imageDataUrl },
          { key: 'user-uploaded-image', value: uploadedUserImage }
        ]
      };

      console.log('ADD TO CART PAYLOAD:', payload);

      /* ================================
         API CALL
      ================================ */
      const response = await axios.post(
        'https://veyraprints.infinite-innovate.com/wp-json/frameandcanvas/v1/print-and-frame-with-matboard-and-glass/add-to-cart',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            setProgress(Math.round((e.loaded * 100) / e.total));
          },
        }
      );

      /* ================================
         RESPONSE
      ================================ */
      if (response.data?.success) {
        setProgress(100);
        setTimeout(() => {
          window.location.href = 'https://veyraprints.infinite-innovate.com/cart';
        }, 700);
      } else {
        throw new Error(response.data?.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Add to cart error:', err);
      alert('Failed to add to cart.');
      setLoading(false);
      setProgress(0);
    }
  };


  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);

  return (
    <div className="product-page">

      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            Loading...
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={previewCanvasW}
          height={previewCanvasH}
          className="real-canvas-preview"
        />
      </div>

      {/* RIGHT PANEL */}
      <div className="product-info-panel">

        <h1 className="product-title">Print, Frame, Matboard & Glass</h1>

        {/* Custom Size */}
        <div className="print-option-row">
          <label className="form-label">Custom Size</label>

          <select
            className="form-select"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
          >
            <option value={CUSTOM_SIZE_OPTION}>
              Custom width × height (cm)
            </option>

            <option value="">
              Auto (Based on image size)
            </option>


            {customSizeOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isCustomWhMode && (
          <div className="print-size-canvas-block">
            <label className="form-label print-size-canvas-block__title">
              Print Size
            </label>

            <div className="print-size-canvas-block__row">

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-w-label"
                >
                  W
                </span>

                <input
                  id="print-canvas-w-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-w-label"
                  value={customWidthCm}
                  onChange={(e) => setCustomWidthCm(e.target.value)}
                />
              </div>

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-h-label"
                >
                  H
                </span>

                <input
                  id="print-canvas-h-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-h-label"
                  value={customHeightCm}
                  onChange={(e) => setCustomHeightCm(e.target.value)}
                />
              </div>

            </div>

            {showApplyCustomCrop && (
              <button
                type="button"
                className="print-size-canvas-block__apply"
                onClick={handleApplyCustomCrop}
              >
                Apply
              </button>
            )}
          </div>
        )}

        {/* Glass */}
        <div className="print-option-row">
          <label className="form-label">Glass</label>
          <select className="form-select" value={glassType} onChange={e => setGlassType(e.target.value)}>

            <option value="">
              -- Select Option --
            </option>

            {glassOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Frame */}
        <div className="print-option-row frame-row">
          <label className="form-label">Frame</label>
          <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>

            <span>
              {selectedFrame ? selectedFrame.name : '-- Select Option --'}
            </span>

            <span className="frame-arrow">›</span>
          </div>
        </div>

        {/* Mat */}
        {[0, 1, 2].map(i => (
          (i === 0 || hasMat(i - 1)) && (
            <div key={i}>
              <div className="print-option-row mat-row">
                <label className="form-label">Mat {i + 1}</label>
                <div
                  className="frame-dropdown-display"
                  onClick={() => {
                    setActiveMatIndex(i);
                    setMatOpen(true);
                  }}
                >
                  <span>{mats[i].mat?.name || 'Select Matboard'}</span>
                  <span className="frame-arrow">›</span>
                </div>
              </div>

              {mats[i].mat && (
                <>
                  {/* Mode Dropdown */}
                  <div className="print-option-row">
                    <label className="form-label">Mat {i + 1} Size Mode</label>
                    <select
                      className="form-select"
                      value={mats[i].mode}
                      onChange={e =>
                        updateMat(i, { mode: e.target.value })
                      }
                    >
                      <option value="equal">Equal Size</option>
                      <option value="custom">Custom Size</option>
                    </select>
                  </div>

                  {/* Equal Mode */}
                  {mats[i].mode === 'equal' && (
                    <div className="print-option-row">
                      <label className="form-label">
                        Width: <strong>{mats[i].thickness} cm</strong>
                      </label>
                      <input
                        type="range"
                        min={getMatMinCm(i)}
                        max={MAT_MAX_CM}
                        step={MAT_STEP_CM}
                        value={mats[i].thickness}
                        onChange={e =>
                          updateMat(i, { thickness: Number(e.target.value) })
                        }
                      />
                    </div>
                  )}

                  {/* Custom Mode */}
                  {mats[i].mode === 'custom' && (
                    <div className="custom-mat-grid">
                      {['top', 'bottom', 'left', 'right'].map(side => (
                        <div key={side} className="print-option-row">
                          <label className="form-label">
                            {side.toUpperCase()}:
                            <strong> {mats[i].custom[side]} cm</strong>
                          </label>
                          <input
                            type="range"
                            min={getMatMinCm(i)}
                            max={MAT_MAX_CM}
                            step={MAT_STEP_CM}
                            value={mats[i].custom[side]}
                            onChange={e =>
                              updateMat(i, {
                                custom: {
                                  ...mats[i].custom,
                                  [side]: Number(e.target.value)
                                }
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        ))}

        {/* V-GROOVE (only if Mat 1 exists) */}
        {hasMat(0) && (
          <div className="print-option-row">
            <label className="form-label">V-Groove</label>

            <select
              className="form-select"
              value={vGrooveType}
              onChange={e => setVGrooveType(e.target.value)}
            >
              {vGrooveOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* DESCRIPTION */}
            <div className="print-description">
              {vGrooveType === 'yes' &&
                'A V-Groove is a decorative angled line cut into the mat board around the artwork, creating a premium gallery-style finish.'}

              {vGrooveType === 'no' &&
                'No decorative groove line will be added to the mat board.'}
            </div>
          </div>
        )}

        {/* SPACER */}
        <div className="print-option-row">
          <label className="form-label">Spacer</label>

          <select
            className="form-select"
            value={spacerType}
            onChange={e => setSpacerType(e.target.value)}
          >
            {spacerOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* DESCRIPTION */}
          <div className="print-description">
            {spacerType === 'no_spacer' &&
              'No spacer will be added between the artwork and the glass.'}

            {spacerType !== 'no_spacer' &&
              'A frame spacer separates your artwork from the glass or acrylic glazing. This creates depth and prevents the art from sticking to the glass over time.'}
          </div>
        </div>

        {/* BACKING */}
        <div className="print-option-row">
          <label className="form-label">Backing</label>

          <select
            className="form-select"
            value={backingType}
            onChange={e => setBackingType(e.target.value)}
          >
            <option value="">
              -- Select Option --
            </option>

            {backingOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>


        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">
            ${finalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)}= ${totalWithGST.toFixed(2)}
          </div>
          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Add to Cart'}
          </button>
        </div>

        <FrameStickSpecPanel frame={selectedFrame} />

      </div>

      {/* FRAME SIDEBAR */}
      {frameOpen && (
        <div className="frame-sidebar open">
          <div className="frame-sidebar-header">
            <h3>Frames</h3>
            <button onClick={() => setFrameOpen(false)}>✕</button>
          </div>

          {frameOptions.map(frame => (
            <div key={frame.id} className="frame-item" onClick={() => {
              setSelectedFrame(frame);
              setFrameOpen(false);
            }}>
              <img src={frame.img} alt={frame.name} />
              <div className="frame-meta">
                <div className="frame-name">{frame.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MAT SIDEBAR */}
      {matOpen && (
        <div className="frame-sidebar open">
          <div className="frame-sidebar-header">
            <h3>Matboard</h3>
            <button onClick={() => setMatOpen(false)}>✕</button>
          </div>


          {matOptions.map(mat => (
            <div key={mat.id} className="frame-item" onClick={() => {
              updateMat(activeMatIndex, {
                mat,
                thickness: mats[activeMatIndex].thickness || getMatMinCm(activeMatIndex)
              });
              setMatOpen(false);
            }}
            >
              <img src={mat.img} alt={mat.name} />
              <div className="frame-meta">
                <div className="frame-name">{mat.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default PrintFrameMatboard;
